import React from 'react';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import {
    buildReputationUrl,
    formatReputationDate,
    getCommentStatusMeta,
    getFeedbackMeta,
    getPatientInitials,
} from '../subparts/reputationQuery';

function OverviewTab({ shortCode, reputationLinks, patientComments, patientFeedbacks }) {
    const reputationUrl = buildReputationUrl(shortCode);

    const handleCopyUrl = async () => {
        if (!reputationUrl) {
            return;
        }

        try {
            await navigator.clipboard.writeText(reputationUrl);
            showSuccess({ title: 'Copied', text: 'Reputation URL copied to clipboard.' });
        } catch (error) {
            console.error('Failed to copy reputation URL', error);
            showError({ title: 'Copy Failed', text: 'Unable to copy URL.' });
        }
    };

    const stats = [
        {
            label: 'Active Links',
            value: reputationLinks?.length ?? 0,
            icon: 'bi-link-45deg',
            iconClass: 'reputation-stat__icon',
        },
        {
            label: 'Total Comments',
            value: patientComments?.totalElements ?? 0,
            icon: 'bi-chat-dots',
            iconClass: 'reputation-stat__icon reputation-stat__icon--cyan',
        },
        {
            label: 'Patient Feedback',
            value: patientFeedbacks?.totalElements ?? 0,
            icon: 'bi-emoji-smile',
            iconClass: 'reputation-stat__icon reputation-stat__icon--amber',
        },
        {
            label: 'Short Code',
            value: shortCode?.shortCode || '—',
            icon: 'bi-braces',
            iconClass: 'reputation-stat__icon reputation-stat__icon--success',
        },
    ];

    const latestComments = patientComments?.content?.slice(0, 3) || [];
    const latestFeedbacks = patientFeedbacks?.content?.slice(0, 3) || [];

    return (
        <div>
            <div className="reputation-section">
                <div>
                    <h2 className="reputation-section__title">
                        <i className="bi bi-speedometer2 me-2"></i>
                        Overview
                    </h2>
                    <p className="reputation-section__subtitle">
                        Snapshot of your reputation links, comments, and patient feedback
                    </p>
                </div>
            </div>

            <div className="reputation-stats">
                {stats.map((stat) => (
                    <div key={stat.label} className="reputation-stat">
                        <span className={stat.iconClass}>
                            <i className={`bi ${stat.icon}`}></i>
                        </span>
                        <div>
                            <span className="reputation-stat__label">{stat.label}</span>
                            <span className="reputation-stat__value">{stat.value}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="reputation-section">
                <div>
                    <h3 className="reputation-section__title">
                        <i className="bi bi-code-slash me-2"></i>
                        Short Code
                    </h3>
                    <p className="reputation-section__subtitle">
                        Your clinic&apos;s unique identifier for reputation URLs
                    </p>
                </div>
            </div>

            {shortCode?.shortCode ? (
                <div className="reputation-shortcode mb-4">
                    <div className="reputation-shortcode__value">{shortCode.shortCode}</div>
                    <p className="text-muted small mb-3">
                        Share this link with patients to collect reviews and feedback.
                    </p>
                    <div className="reputation-shortcode__url mb-3">{reputationUrl}</div>
                    <button type="button" className="btn btn-sm btn-accent rounded-pill" onClick={handleCopyUrl}>
                        <i className="bi bi-copy me-1"></i>
                        Copy URL
                    </button>
                </div>
            ) : (
                <div className="ecx-page-empty mb-4">
                    <span className="ecx-page-empty__icon">
                        <i className="bi bi-exclamation-triangle"></i>
                    </span>
                    <h3 className="h6 fw-bold text-dark mb-1">No short code configured</h3>
                    <p className="mb-0 small">Contact support to set up your reputation short code.</p>
                </div>
            )}

            <div className="reputation-section">
                <div>
                    <h3 className="reputation-section__title">
                        <i className="bi bi-activity me-2"></i>
                        Recent Activity
                    </h3>
                    <p className="reputation-section__subtitle">Latest comments and feedback from patients</p>
                </div>
            </div>

            <div className="reputation-activity-grid">
                <div className="reputation-activity-card">
                    <div className="reputation-activity-card__header">Latest Comments</div>
                    <div className="reputation-activity-card__body">
                        {latestComments.length > 0 ? (
                            latestComments.map((comment) => {
                                const statusMeta = getCommentStatusMeta(comment.status);

                                return (
                                    <div key={comment.id} className="reputation-activity-item">
                                        <span className="reputation-avatar">
                                            {getPatientInitials(comment.firstName, comment.lastName)}
                                        </span>
                                        <div className="reputation-activity-item__meta">
                                            <div className="d-flex align-items-start justify-content-between gap-2">
                                                <h4 className="reputation-activity-item__title">
                                                    {comment.firstName} {comment.lastName}
                                                </h4>
                                                <span className={statusMeta.className}>
                                                    <i className={`bi ${statusMeta.icon}`}></i>
                                                    {statusMeta.label}
                                                </span>
                                            </div>
                                            <p className="reputation-activity-item__text">{comment.notes || '—'}</p>
                                            <span className="reputation-activity-item__date">
                                                {formatReputationDate(comment.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-4 text-muted small">No comments yet</div>
                        )}
                    </div>
                </div>

                <div className="reputation-activity-card">
                    <div className="reputation-activity-card__header">Recent Feedback</div>
                    <div className="reputation-activity-card__body">
                        {latestFeedbacks.length > 0 ? (
                            latestFeedbacks.map((feedback) => {
                                const feedbackMeta = getFeedbackMeta(feedback.feedback);

                                return (
                                    <div key={feedback.id} className="reputation-activity-item">
                                        <span className="reputation-avatar">{feedback.patientName?.charAt(0) || '?'}</span>
                                        <div className="reputation-activity-item__meta">
                                            <div className="d-flex align-items-start justify-content-between gap-2">
                                                <h4 className="reputation-activity-item__title">{feedback.patientName}</h4>
                                                <span className={feedbackMeta.className}>
                                                    <i className={`bi ${feedbackMeta.icon}`}></i>
                                                    {feedbackMeta.label}
                                                </span>
                                            </div>
                                            <span className="reputation-activity-item__date">
                                                {formatReputationDate(feedback.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-4 text-muted small">No feedback yet</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OverviewTab;
